/**
 * StewardshipActionsCard — iter84.72
 *
 * Phase 4 (Audit) card: merged, newest-first feed of stewardship
 * actions from the Values Engine + Obligations Engine audit trails
 * (classifications, severity changes, dispositions). Read-only.
 * Backend: GET /api/release-ops/stewardship-actions.
 */
import { useEffect, useState } from "react";
import { ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";

const SOURCE_STYLES = {
    values: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    obligations: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

export function StewardshipActionsCard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.releaseOpsStewardshipActions()
            .then(setData)
            .catch((e) => setError(e?.response?.data?.detail || "Failed to load"));
    }, []);

    const actions = data?.actions || [];

    return (
        <CollapsibleCard
            testId="stewardship-actions-card"
            icon={ClipboardList}
            title="Stewardship actions"
            badge={data ? `${data.total}` : "…"}
            badgeClass="bg-zinc-700/30 text-zinc-300 border-zinc-600"
        >
            <p className="text-[11px] text-zinc-400 leading-snug mb-2">
                Merged audit trail of the <span className="text-sky-300 font-bold">Values Engine</span> and{" "}
                <span className="text-violet-300 font-bold">Obligations Engine</span> — every classification,
                severity change, and disposition, newest first. Act on entries in the Values / Obligations tabs.
            </p>
            {error && (
                <p className="text-xs text-rose-400" data-testid="stewardship-actions-error">{error}</p>
            )}
            {!error && data && actions.length === 0 && (
                <p className="text-xs text-zinc-500 italic" data-testid="stewardship-actions-empty">
                    No stewardship actions recorded yet.
                </p>
            )}
            {actions.length > 0 && (
                <ul className="space-y-1" data-testid="stewardship-actions-list">
                    {actions.slice(0, 15).map((a, i) => (
                        <li
                            key={`${a.source}-${a.timestamp}-${i}`}
                            className="text-[11px] py-1 border-b border-[#1a1a1a] last:border-b-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
                            data-testid={`stewardship-action-row-${i}`}
                        >
                            <span className={`px-1.5 py-0 rounded border text-[9px] uppercase font-bold ${SOURCE_STYLES[a.source] || SOURCE_STYLES.values}`}>
                                {a.source}
                            </span>
                            <span className="text-white font-bold">{a.entry || "—"}</span>
                            <span className="text-zinc-400">{a.action || "—"}</span>
                            {(a.from || a.to) && (
                                <span className="font-mono text-[10px] text-zinc-500">
                                    {a.from || "—"} → {a.to || "—"}
                                </span>
                            )}
                            {a.note && <span className="text-zinc-500 italic">“{a.note}”</span>}
                            <span className="text-zinc-600 ml-auto font-mono text-[9px]">
                                {a.timestamp || ""}{a.actor ? ` · ${a.actor}` : ""}
                            </span>
                        </li>
                    ))}
                    {actions.length > 15 && (
                        <li className="text-[10px] text-zinc-500 italic pt-1">
                            …and {actions.length - 15} more (full trails live in the Values / Obligations tabs)
                        </li>
                    )}
                </ul>
            )}
        </CollapsibleCard>
    );
}
